<script setup>
/* global axios */
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { exportReportToPdf } from './helpers/reportPdf';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';

const route = useRoute();
const accountId = route.params.accountId;
const STORAGE_KEY = 'tickets_list_report_columns';

const from = ref(0);
const to = ref(0);

const humanize = v =>
  v === null || v === undefined || v === '' ? '—'
    : String(v).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const FIXED_COLUMNS = [
  { key: 'ticket_id', label: 'Ticket ID', type: 'link' },
  { key: 'subject', label: 'Subject' },
  { key: 'contact_name', label: 'Contact', get: r => r.contact?.name },
  { key: 'assignee_name', label: 'Agent' },
  { key: 'status', label: 'Status', get: r => humanize(r.status) },
  { key: 'ticket_type', label: 'Type', get: r => humanize(r.ticket_type) },
  { key: 'priority', label: 'Priority', get: r => humanize(r.priority) },
  { key: 'resolution_time', label: 'Resolution Time', type: 'duration' },
  { key: 'properties', label: 'Support Request' },
  { key: 'created_at', label: 'Created', type: 'date' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'tags', label: 'Tags' },
  { key: 'conversation_id', label: 'Conversation', get: r => (r.conversation_id ? `#${r.conversation_id}` : '—') },
  { key: 'company_name', label: 'Company Name', get: r => r.contact?.company_name },
  { key: 'company_domain', label: 'Company Domain', get: r => r.contact?.company_domain },
  { key: 'contact_email', label: 'Email', get: r => r.contact?.email },
  { key: 'contact_phone', label: 'Phone', get: r => r.contact?.phone_number },
  { key: 'city', label: 'City', get: r => r.contact?.city },
  { key: 'country', label: 'Country', get: r => r.contact?.country },
];

const DEFAULT_VISIBLE = [
  'ticket_id', 'subject', 'contact_name', 'assignee_name',
  'status', 'ticket_type', 'priority', 'resolution_time', 'created_at',
];

const rows = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(25);
const loading = ref(false);

const showColumnPicker = ref(false);
const columnPickerRef = ref(null);
const visibleColumns = ref([...DEFAULT_VISIBLE]);
onClickOutside(columnPickerRef, () => { showColumnPicker.value = false; });

onMounted(() => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(stored) && stored.length) visibleColumns.value = stored;
  } catch (_) {
    visibleColumns.value = [...DEFAULT_VISIBLE];
  }
});

const customAttrKeys = computed(() => {
  const set = new Set();
  rows.value.forEach(r =>
    Object.keys(r.contact?.custom_attributes || {}).forEach(k => set.add(k))
  );
  return [...set];
});

const allColumns = computed(() => [
  ...FIXED_COLUMNS,
  ...customAttrKeys.value.map(k => ({
    key: `custom:${k}`,
    label: k,
    get: r => r.contact?.custom_attributes?.[k],
  })),
]);

const activeColumns = computed(() =>
  visibleColumns.value
    .map(key => allColumns.value.find(c => c.key === key))
    .filter(Boolean)
);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)));

// ---- Filters ----------------------------------------------------------------
const FILTER_FIELDS = [
  { key: 'subject', label: 'Subject' },
  { key: 'contact_name', label: 'Contact' },
  { key: 'assignee_name', label: 'Agent' },
  { key: 'status', label: 'Status (open/pending/resolved/closed)' },
  { key: 'ticket_type', label: 'Type' },
  { key: 'priority', label: 'Priority (none/low/medium/high/urgent)' },
  { key: 'properties', label: 'Support Request (any property)' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'company_domain', label: 'Company Domain' },
  { key: 'contact_email', label: 'Email' },
  { key: 'contact_phone', label: 'Phone' },
];

// Real status values come from the account's ticket field settings (returned in meta);
// these are the fallback until the first fetch populates them.
const statusOptions = ref([
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]);

const STATUS_FILTER_OPTIONS = computed(() => [
  { value: 'unresolved', label: 'Unresolved (not resolved / closed)' },
  ...statusOptions.value,
]);

const filters = ref([]);
const showFilterPanel = ref(false);
const filterPanelRef = ref(null);
onClickOutside(filterPanelRef, () => { showFilterPanel.value = false; });

const filterFields = computed(() => [
  ...FILTER_FIELDS,
  ...customAttrKeys.value.map(k => ({ key: `custom:${k}`, label: k })),
]);

const addFilter = () => filters.value.push({ field: '', value: '' });
const removeFilter = i => filters.value.splice(i, 1);

const buildFilterPayload = () =>
  filters.value
    .map(f => (f.field && f.value ? { field: f.field, value: f.value } : null))
    .filter(Boolean);

const appliedFilterCount = ref(0);
const applyFilters = () => {
  appliedFilterCount.value = buildFilterPayload().length;
  page.value = 1;
  fetchData();
  showFilterPanel.value = false;
};
const clearFilters = () => {
  filters.value = [];
  appliedFilterCount.value = 0;
  page.value = 1;
  fetchData();
};

const formatDate = epoch => (epoch ? new Date(epoch * 1000).toLocaleString() : '—');

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return '—';
  let s = Math.round(seconds);
  if (s < 60) return `${s} Sec`;
  const m = Math.floor(s / 60);
  s %= 60;
  if (m < 60) return `${m} Min ${s} Sec`;
  const h = Math.floor(m / 60);
  return `${h} Hr ${m % 60} Min`;
};

const cellValue = (col, row) => {
  if (col.type === 'date') return formatDate(row.created_at);
  if (col.type === 'duration') return formatDuration(row[col.key]);
  const raw = col.get ? col.get(row) : row[col.key];
  return raw === null || raw === undefined || raw === '' ? '—' : raw;
};
const csvCellValue = (col, row) => {
  if (col.type === 'link') return row.ticket_id;
  if (col.type === 'date') return row.created_at ? formatDate(row.created_at) : '';
  if (col.type === 'duration') {
    const v = row[col.key];
    return v === null || v === undefined ? '' : formatDuration(v);
  }
  const raw = col.get ? col.get(row) : row[col.key];
  return raw === null || raw === undefined ? '' : raw;
};

const toggleColumn = key => {
  visibleColumns.value = visibleColumns.value.includes(key)
    ? visibleColumns.value.filter(k => k !== key)
    : [...visibleColumns.value, key];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns.value));
};

const requestParams = (pageNum, per) => ({
  since: from.value,
  until: to.value,
  page: pageNum,
  ...(per ? { per_page: per } : {}),
  filters: JSON.stringify(buildFilterPayload()),
});

const fetchData = async () => {
  if (!from.value || !to.value) return;
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/tickets_list`,
      { params: requestParams(page.value) }
    );
    rows.value = data.payload || [];
    total.value = data.meta?.count || 0;
    perPage.value = data.meta?.per_page || 25;
    if (Array.isArray(data.meta?.status_options) && data.meta.status_options.length) {
      statusOptions.value = data.meta.status_options;
    }
  } catch (_) {
    rows.value = [];
    total.value = 0;
    useAlert('Could not load the tickets report.');
  } finally {
    loading.value = false;
  }
};

const exporting = ref(false);
const exportCsv = async () => {
  if (!from.value || !to.value) return;
  exporting.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/tickets_list`,
      { params: requestParams(1, 10000) }
    );
    const list = data.payload || [];
    const cols = activeColumns.value;
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = cols.map(c => esc(c.label)).join(',');
    const body = list.map(r => cols.map(c => esc(csvCellValue(c, r))).join(',')).join('\n');
    const blob = new Blob([`﻿${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tickets-report-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (_) {
    useAlert('Could not export the report.');
  } finally {
    exporting.value = false;
  }
};

const pdfExporting = ref(false);
const dateRangeLabel = () => {
  if (!from.value || !to.value) return '';
  return `${new Date(from.value * 1000).toLocaleDateString()} – ${new Date(to.value * 1000).toLocaleDateString()}`;
};
const exportPdf = async () => {
  if (!from.value || !to.value) return;
  pdfExporting.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId}/reports/tickets_list`,
      { params: requestParams(1, 10000) }
    );
    const list = data.payload || [];
    const cols = activeColumns.value;
    exportReportToPdf({
      title: 'Tickets Report',
      dateRange: dateRangeLabel(),
      headers: cols.map(c => c.label),
      rows: list.map(r => cols.map(c => csvCellValue(c, r))),
    });
  } catch (_) {
    useAlert('Could not export the PDF.');
  } finally {
    pdfExporting.value = false;
  }
};

const goToPage = p => {
  if (p < 1 || p > totalPages.value || p === page.value) return;
  page.value = p;
  fetchData();
};

const onFilterChange = ({ from: f, to: t }) => {
  from.value = f;
  to.value = t;
  page.value = 1;
  fetchData();
};
</script>

<template>
  <ReportHeader :header-title="$t('TICKETS_REPORT.HEADER')" />
  <ReportFilters
    :show-entity-filter="false"
    :show-group-by="false"
    :show-business-hours="false"
    @filter-change="onFilterChange"
  />

  <div class="flex flex-col gap-3 py-4">
    <div class="flex items-center justify-between">
      <span class="text-sm text-n-slate-11">{{ total }} tickets</span>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50"
          :disabled="exporting"
          @click="exportCsv"
        >
          <span class="i-lucide-download size-4" /> {{ exporting ? 'Exporting…' : 'CSV' }}
        </button>
        <button
          class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-50"
          :disabled="pdfExporting"
          @click="exportPdf"
        >
          <span class="i-lucide-file-text size-4" /> {{ pdfExporting ? 'Preparing…' : 'PDF' }}
        </button>
        <div ref="filterPanelRef" class="relative">
          <button
            class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
            @click="showFilterPanel = !showFilterPanel"
          >
            <span class="i-lucide-filter size-4" /> Filters
            <span
              v-if="appliedFilterCount"
              class="ml-1 px-1.5 rounded-full bg-n-blue-9 text-white text-xs"
            >{{ appliedFilterCount }}</span>
          </button>
          <div
            v-if="showFilterPanel"
            class="absolute right-0 z-30 mt-1 w-[420px] max-h-[30rem] overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-3"
          >
            <p class="text-xs uppercase font-semibold text-n-slate-10 mb-2">Filter tickets</p>
            <div v-for="(f, i) in filters" :key="i" class="mb-2 p-2 rounded-lg border border-n-weak">
              <div class="flex items-center gap-2">
                <select
                  v-model="f.field"
                  class="flex-1 min-w-0 px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Select field…</option>
                  <option v-for="ff in filterFields" :key="ff.key" :value="ff.key">{{ ff.label }}</option>
                </select>
                <button class="shrink-0 text-n-slate-10 hover:text-n-slate-12 px-1" @click="removeFilter(i)">✕</button>
              </div>
              <template v-if="f.field">
                <select
                  v-if="f.field === 'status'"
                  v-model="f.value"
                  class="mt-2 w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option value="">Any status…</option>
                  <option v-for="o in STATUS_FILTER_OPTIONS" :key="o.value" :value="o.value">
                    {{ o.label }}
                  </option>
                </select>
                <input
                  v-else
                  v-model="f.value"
                  type="text"
                  placeholder="contains…"
                  class="mt-2 w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </template>
            </div>
            <button class="mt-1 text-sm text-n-blue-text hover:underline" @click="addFilter">+ Add filter</button>
            <div class="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-n-weak">
              <button class="px-3 py-1.5 text-sm rounded-lg text-n-slate-11 hover:bg-n-slate-3" @click="clearFilters">Clear</button>
              <button class="px-4 py-1.5 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10" @click="applyFilters">Apply</button>
            </div>
          </div>
        </div>
        <div ref="columnPickerRef" class="relative">
          <button
            class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-slate-3"
            @click="showColumnPicker = !showColumnPicker"
          >
            <span class="i-lucide-columns-3 size-4" /> Columns
          </button>
          <div
            v-if="showColumnPicker"
            class="absolute right-0 z-30 mt-1 w-64 max-h-96 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-2"
          >
            <p class="px-2 py-1 text-xs uppercase font-semibold text-n-slate-10">Show columns</p>
            <label
              v-for="col in allColumns"
              :key="col.key"
              class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-slate-3 cursor-pointer"
            >
              <input type="checkbox" :checked="visibleColumns.includes(col.key)" @change="toggleColumn(col.key)" />
              <span class="text-sm text-n-slate-12">{{ col.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              v-for="col in activeColumns"
              :key="col.key"
              class="text-left font-semibold text-n-slate-11 px-4 py-3 whitespace-nowrap"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="activeColumns.length" class="px-4 py-8 text-center text-n-slate-10">Loading…</td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="activeColumns.length" class="px-4 py-8 text-center text-n-slate-10">No tickets in this range.</td>
          </tr>
          <tr
            v-for="row in rows"
            v-else
            :key="row.ticket_id"
            class="border-b border-n-weak last:border-b-0 hover:bg-n-slate-2"
          >
            <td
              v-for="col in activeColumns"
              :key="col.key"
              class="px-4 py-3 whitespace-nowrap text-n-slate-12"
            >
              <router-link
                v-if="col.type === 'link'"
                :to="{ name: 'ticket_detail', params: { ticketId: row.ticket_id } }"
                class="text-n-blue-text hover:underline"
              >
                #{{ row.ticket_id }}
              </router-link>
              <span v-else>{{ cellValue(col, row) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > 0" class="flex items-center justify-end gap-2 text-sm text-n-slate-11">
      <button class="px-2 py-1 rounded border border-n-weak disabled:opacity-40" :disabled="page <= 1" @click="goToPage(page - 1)">‹</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button class="px-2 py-1 rounded border border-n-weak disabled:opacity-40" :disabled="page >= totalPages" @click="goToPage(page + 1)">›</button>
    </div>
  </div>
</template>
